import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { Coins } from "lucide-react";
import { useTranslationApp } from "../../lib/i18next";
import {
  BUTTON_CLOSE,
  LOADING_PLAN_INFORMATION,
  PAYWALL_INSUFFICIENT_CREDITS_SUBTITLE,
  PAYWALL_INSUFFICIENT_CREDITS_TITLE,
  PAYWALL_INSUFFICIENT_CREDITS_UPGRADE_DESCRIPTION,
  PAYWALL_INSUFFICIENT_CREDITS_UPGRADE_TITLE,
} from "../../i18n/keysTranslations";
import { PLAN_ENTERPRISE, PLAN_TYPE_CREDITS } from "../../utils/constants";
import { useSelectorApp } from "../../lib/redux";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import Loader from "../Loaders/Loader";
import PricingCard from "./PricingCard";
import PaywallCreditPackages from "./PaywallCreditPackages";
import { getFeaturedPlanId } from "./PricingCardsContainer";

const PricingInsufficientCredits = ({
  plans = [],
  isLoading = false,
  isUpgrade = false,
  recurrent,
  onUpgradeSubscription = () => {},
  checkoutPromo = null,
  isCheckoutPromoLoading = false,
  onCloseModal = () => {},
  footer = null,
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const isCreditsPlan = useSelectorApp(
    (state) => state.payments?.typeOfPlans === PLAN_TYPE_CREDITS
  );
  const headerTextColor = theme.palette.text.primary;
  const visiblePlans = plans.filter((plan) => plan.id !== PLAN_ENTERPRISE);
  const hasUpgradeCta = visiblePlans.some(
    (plan) =>
      plan.subscriptionTierRelation !== "below" &&
      plan.subscriptionTierRelation !== "current"
  );
  // Keep Enterprise when no other tier still has an upgrade CTA.
  const plansToRender =
    visiblePlans.length && hasUpgradeCta ? visiblePlans : plans;
  const featuredPlanID = getFeaturedPlanId(plans);
  const cardColumns = Math.min(plansToRender.length, 3) || 1;

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          backgroundColor: "background.paper",
          color: headerTextColor,
          px: { xs: 3, lg: 5 },
          py: { xs: 2, sm: 2.5 },
          pr: { xs: 7, sm: 9 },
        }}
      >
        <IconButton
          aria-label={t(BUTTON_CLOSE)}
          onClick={onCloseModal}
          sx={{
            position: "absolute",
            top: { xs: 6, sm: 10 },
            right: { xs: 6, sm: 10 },
            color: `${headerTextColor} !important`,
            "&:hover": {
              backgroundColor: alpha(headerTextColor, 0.12),
              color: `${headerTextColor} !important`,
            },
          }}
        >
          <CloseIcon
            sx={{ color: `${headerTextColor} !important`, fontSize: 26 }}
          />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          <Box
            sx={{
              width: { xs: 48, sm: 64 },
              height: { xs: 48, sm: 64 },
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              backgroundColor: alpha(headerTextColor, 0.15),
              color: headerTextColor,
            }}
          >
            <Coins size={28} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="h2"
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "inherit",
                fontSize: { xs: "1.05rem", sm: "1.45rem" },
                lineHeight: 1.2,
              }}
            >
              {t(PAYWALL_INSUFFICIENT_CREDITS_TITLE)}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                color: alpha(headerTextColor, 0.85),
                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                lineHeight: 1.4,
              }}
            >
              {t(PAYWALL_INSUFFICIENT_CREDITS_SUBTITLE)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: isCreditsPlan ? "minmax(0, 1fr) 300px" : "1fr",
          },
        }}
      >
        <Box
          sx={{
            py: { xs: 2, lg: 3 },
            px: { xs: 3, lg: 5 },
            minWidth: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.3 }}
          >
            {t(PAYWALL_INSUFFICIENT_CREDITS_UPGRADE_TITLE)}
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              mb: 1,
              color: "text.secondary",
              fontSize: "0.9rem",
              lineHeight: 1.45,
            }}
          >
            {t(PAYWALL_INSUFFICIENT_CREDITS_UPGRADE_DESCRIPTION)}
          </Typography>

          {isLoading ? (
            <Loader
              size={40}
              hasMessage={true}
              message={t(LOADING_PLAN_INFORMATION)}
            />
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  // At most `cardColumns` per row, never narrower than 210px:
                  // extra cards wrap instead of shrinking.
                  sm: `repeat(auto-fit, minmax(max(210px, calc((100% - ${(cardColumns - 1) * 12}px) / ${cardColumns})), 1fr))`,
                },
                gap: 1.5,
                pt: 3,
                alignItems: "stretch",
              }}
            >
              {plansToRender.map((plan) => (
                <PricingCard
                  key={plan.id}
                  variant="compact"
                  data={plan}
                  recurrent={recurrent}
                  isUpgrade={isUpgrade}
                  onUpgradeSubscription={onUpgradeSubscription}
                  featuredPlanID={featuredPlanID}
                  subscriptionTierRelation={plan.subscriptionTierRelation}
                  checkoutPromo={checkoutPromo}
                  isCheckoutPromoLoading={isCheckoutPromoLoading}
                />
              ))}
            </Box>
          )}
        </Box>

        {isCreditsPlan && (
          <Box
            sx={{
              pt: { xs: 2, lg: 3 },
              // lg: 24px grid padding + 16px PricingCard CardActions padding,
              // so the buy button lines up with the plan CTA buttons.
              pb: { xs: 2, lg: 5 },
              px: { xs: 3, lg: 3 },
              minWidth: 0,
              display: "flex",
              borderTop: { xs: `1px solid ${INPUT_COLOR_BORDER}`, lg: "none" },
              borderLeft: { xs: "none", lg: `1px solid ${INPUT_COLOR_BORDER}` },
            }}
          >
            <PaywallCreditPackages />
          </Box>
        )}
      </Box>
      {footer && <Box sx={{ px: { xs: 3, lg: 5 } }}>{footer}</Box>}
    </Box>
  );
};

export default PricingInsufficientCredits;
